import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { parseISO } from 'date-fns';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { SchedulingStep } from './create/SchedulingStep';
import { ResourceAssignmentFields } from './create/ResourceAssignmentFields';
import type { SelectedServiceData } from './create/ServiceStep';
import { getDefaultScheduledAt, toDateTimeLocal } from './WorkOrderCreateModal';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useServices } from '@/hooks/useServices';
import { useMechanicsWithSkills } from '@/hooks/useStaffUsers';
import { useAllLifts } from '@/hooks/useLifts';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/hooks/useNotify';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import type { AcceptQuoteData } from '@/hooks/useBookings';
import type { SchedulingType } from '@/types';
import { formatDuration, parseDurationHours, sumHours } from '@/utils/duration';
import { eurosToCents } from '@/utils/money';
import { useExactTimeOutsideShiftWarning } from '@/features/calendar/outsideShift';
import { CertificateOverrideDialog } from './CertificateOverrideDialog';
import {
  isCertificateOverrideForbiddenError,
  parseCertificateGateError,
  type CertificateRequiredError,
  type SkillGateOverride,
} from './certificateGateError';

export type AcceptQuoteConfirmData = AcceptQuoteData & {
  skillGateOverride?: SkillGateOverride;
};

interface AcceptQuoteScheduleDialogProps {
  open: boolean;
  workOrder: WorkOrderDetail;
  onConfirm: (data: AcceptQuoteConfirmData, expectedUpdatedAt: string) => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  quoteVersionLabel?: string;
  errorMessage?: string | null;
  /**
   * A slot chosen before the dialog opened — a calendar cell (#2653). Seeds
   * the arrival time and, when it carries a clock time, switches to an exact
   * booking; the mechanic seeds the assignment. Omitted: the usual defaults.
   */
  initialScheduledAt?: Date | undefined;
  initialMechanicId?: string | undefined;
}

type WorkOrderService = WorkOrderDetail['services'][number];

/** Draft of one service's planned hours while the accept dialog is open. */
interface ServicePlannedDraft {
  bookingServiceId: string;
  name: string;
  input: string;
  /** Parsed hours; `''` is empty/invalid. Restore-to-derived is not offered here. */
  hours: number | '';
  originalHours: number | null;
  originalManual: boolean;
  manual: boolean;
}

function seedServiceDrafts(services: WorkOrderService[]): ServicePlannedDraft[] {
  return services.map((service) => {
    const hours = service.estimatedDurationHours ?? null;
    return {
      bookingServiceId: service.bookingServiceId,
      name: service.name,
      input: hours != null ? String(hours) : '',
      hours: hours ?? '',
      originalHours: hours,
      originalManual: service.plannedHoursManual === true,
      manual: service.plannedHoursManual === true,
    };
  });
}

function hasInvalidManualDuration(draft: ServicePlannedDraft): boolean {
  return draft.hours === '' && draft.manual;
}

function effectiveDraftHours(draft: ServicePlannedDraft): number | null {
  if (draft.hours === '') return null;
  return draft.hours;
}

function changedServiceSchedules(
  drafts: ServicePlannedDraft[],
): Record<string, { estimatedDurationHours: number }> | undefined {
  const result: Record<string, { estimatedDurationHours: number }> = {};
  for (const draft of drafts) {
    if (typeof draft.hours !== 'number') continue;
    // Typing the same hours as a derived value is still an explicit latch:
    // the backend sets plannedHoursManual from a non-null duration (#2667).
    const latchedManual = draft.manual && !draft.originalManual;
    if (draft.hours !== draft.originalHours || latchedManual) {
      result[draft.bookingServiceId] = { estimatedDurationHours: draft.hours };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function QuoteServicePlannedHours({
  drafts,
  totalHours,
  onHoursChange,
}: {
  drafts: ServicePlannedDraft[];
  totalHours: number;
  onHoursChange: (bookingServiceId: string, raw: string) => void;
}) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('detail.plannedHours')}: {formatDuration(totalHours)}
      </Typography>
      {drafts.map((draft) => {
        const fieldLabel = t('detail.servicePlannedHours', { name: draft.name });
        const invalid = hasInvalidManualDuration(draft);
        return (
          <TextField
            key={draft.bookingServiceId}
            type="text"
            size="small"
            label={fieldLabel}
            error={invalid}
            helperText={
              invalid
                ? (draft.input.trim() === ''
                  ? tc('validation.required')
                  : t('create.estimatedDurationHint'))
                : draft.manual
                  ? t('detail.plannedHoursManual')
                  : t('detail.plannedHoursDerived')
            }
            value={draft.input}
            onChange={(event) => {
              onHoursChange(draft.bookingServiceId, event.target.value);
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">h</InputAdornment>
                ),
              },
              htmlInput: {
                inputMode: 'decimal',
                'aria-label': fieldLabel,
              },
            }}
          />
        );
      })}
    </Box>
  );
}

/**
 * "Accept and schedule" — the dialog that turns a quote (#1023) into a
 * booking (quote -> pending). Reuses SchedulingStep, the same content and
 * rules as WorkOrderCreateModal's Aikataulu step, and the shared mechanic +
 * lift selectors (#2647). The schedule, the resources and the status change
 * are saved atomically by the caller.
 *
 * This is the ONLY place a quote gets its time, duration, mechanic and lift:
 * the schedule bar shows none of those for a quote any more, because its
 * stored time is a technical default and editing it there produced a
 * second, conflicting schedule (#2647).
 *
 * - Service-backed quotes edit planned hours per service (#2667) from
 *   `workOrder.services[].estimatedDurationHours` / `plannedHoursManual`.
 *   Only changed rows go on the wire as positive numeric `serviceSchedules`;
 *   restore-to-derived is left to the post-accept schedule bar. The overall
 *   duration field is hidden and availability uses the effective sum.
 * - A no-service quote still edits overall `estimatedDurationHours`.
 * - Mechanic and lift are one choice for the whole work order (#1984/#1993),
 *   seeded from the booking-level assignments. They are sent only when the
 *   user changes them and only when the user may assign; a user without
 *   `bookings.assign` never sees the selectors, so the request never earns
 *   a 403 for a value the user did not touch.
 */
export function AcceptQuoteScheduleDialog({
  open,
  workOrder,
  onConfirm,
  onCancel,
  loading,
  quoteVersionLabel,
  errorMessage,
  initialScheduledAt,
  initialMechanicId,
}: AcceptQuoteScheduleDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const { defaultSchedulingType, workDurationStepMinutes, isPending: settingsPending } = useTenantSettings();
  const canAssign = useAuthStore((s) => s.hasPermission('bookings.assign'));
  const { data: services = [] } = useServices({ isActive: true, sortBy: 'usage' });
  const { data: mechanics = [] } = useMechanicsWithSkills();
  const { data: lifts = [] } = useAllLifts();

  const [scheduledAtStr, setScheduledAtStr] = useState('');
  const [vehicleDropoffAtStr, setVehicleDropoffAtStr] = useState('');
  const [schedulingType, setSchedulingType] = useState<SchedulingType>('floating');
  const [pickupAtStr, setPickupAtStr] = useState('');
  const [pickupAtTouched, setPickupAtTouched] = useState(false);
  const [estimatedDurationHours, setEstimatedDurationHours] = useState(0);
  const [serviceDrafts, setServiceDrafts] = useState<ServicePlannedDraft[]>([]);
  const [mechanicId, setMechanicId] = useState('');
  const [liftId, setLiftId] = useState('');
  const [customerWaits, setCustomerWaits] = useState(false);
  const [allowMinorMaintenance, setAllowMinorMaintenance] = useState(false);
  const [jointInspectionRequested, setJointInspectionRequested] = useState(false);
  const [preapprovedLimitInput, setPreapprovedLimitInput] = useState('');
  const openedQuoteId = useRef<string | null>(null);
  const [reviewedUpdatedAt, setReviewedUpdatedAt] = useState<string | null>(null);
  const pendingAcceptRef = useRef<AcceptQuoteConfirmData | null>(null);
  const [certificateGate, setCertificateGate] = useState<CertificateRequiredError | null>(null);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  // What the quote already carries. Only a change from these is sent.
  const seededMechanicId =
    workOrder.assignedMechanicId ?? workOrder.services[0]?.assignedMechanicId ?? '';
  const seededLiftId = workOrder.assignedLiftId ?? '';
  const hasServices = (workOrder.services?.length ?? 0) > 0;

  // Re-seed from the quote every time the dialog opens — a sensible new
  // default schedule (never the quote's hidden/technical time), plus the
  // quote's duration and mechanic/lift assignments.
  useEffect(() => {
    if (!open) {
      openedQuoteId.current = null;
      return;
    }
    // Wait for the workshop default before opening an editable draft. Once
    // seeded, neither settings nor quote refreshes may reset user input.
    if (settingsPending) return;
    // A background refresh must not replace the version the user reviewed
    // or erase their draft. A changed quote requires closing and reopening.
    if (openedQuoteId.current === workOrder.id) return;
    openedQuoteId.current = workOrder.id;
    setReviewedUpdatedAt(workOrder.updatedAt);
    const defaultAt = initialScheduledAt ? toDateTimeLocal(initialScheduledAt) : getDefaultScheduledAt();
    const day = defaultAt.split('T')[0] ?? defaultAt;
    // A calendar cell with a clock time is an exact appointment; a day-only
    // pick (or no pick) follows the workshop default (#2653).
    const clicksClockTime =
      !!initialScheduledAt && (initialScheduledAt.getHours() !== 0 || initialScheduledAt.getMinutes() !== 0);
    const type: SchedulingType = clicksClockTime ? 'exact' : defaultSchedulingType;
    setSchedulingType(type);
    setScheduledAtStr(type === 'floating' ? `${day}T00:00` : defaultAt);
    setPickupAtStr(defaultAt);
    setVehicleDropoffAtStr(workOrder.vehicleDropoffAt ? toDateTimeLocal(new Date(workOrder.vehicleDropoffAt)) : '');
    setPickupAtTouched(false);
    setEstimatedDurationHours(workOrder.estimatedDurationHours ?? 0);
    setServiceDrafts(seedServiceDrafts(workOrder.services ?? []));
    setMechanicId(initialMechanicId ?? seededMechanicId);
    setLiftId(seededLiftId);
    setCustomerWaits(workOrder.customerWaits ?? false);
    setAllowMinorMaintenance(workOrder.allowMinorMaintenance ?? false);
    setJointInspectionRequested(quoteVersionLabel ? workOrder.jointInspectionRequested ?? false : false);
    setPreapprovedLimitInput(quoteVersionLabel && workOrder.preapprovedAdditionalWorkLimitCents != null ? String(workOrder.preapprovedAdditionalWorkLimitCents / 100) : '');
    setCertificateGate(null);
    pendingAcceptRef.current = null;
  }, [
    open,
    workOrder,
    defaultSchedulingType,
    settingsPending,
    seededMechanicId,
    seededLiftId,
    initialScheduledAt,
    initialMechanicId,
    quoteVersionLabel,
  ]);

  // Mirror scheduledAt into pickup until the user has touched the field
  // (same behavior as WorkOrderCreateModal's Aikataulu step).
  useEffect(() => {
    if (pickupAtTouched) return;
    if (scheduledAtStr && scheduledAtStr !== pickupAtStr) {
      setPickupAtStr(scheduledAtStr);
    }
  }, [scheduledAtStr, pickupAtTouched, pickupAtStr]);

  const livePlannedTotal = useMemo(() => {
    if (!hasServices) return estimatedDurationHours;
    return sumHours(serviceDrafts.map(effectiveDraftHours));
  }, [hasServices, estimatedDurationHours, serviceDrafts]);

  // SchedulingStep's availability lookups take the services; the mechanic and
  // lift ride on every row, as in creation (#1984). Per-service planned hours
  // feed the availability preview as their effective sum (#2667).
  const selectedServices = useMemo<SelectedServiceData[]>(
    () => {
      const draftById = new Map(serviceDrafts.map((draft) => [draft.bookingServiceId, draft]));
      return workOrder.services.map((svc) => {
        const draft = draftById.get(svc.bookingServiceId);
        return {
          rowId: svc.bookingServiceId,
          serviceId: svc.id,
          serviceName: svc.name,
          estimatedDurationHours: draft ? effectiveDraftHours(draft) : (svc.estimatedDurationHours ?? null),
          durationType: 'fixed',
          durationOptions: [],
          laborItemCount: 0,
          extrasCount: 0,
          mechanicId,
          liftId,
        };
      });
    },
    [workOrder.services, mechanicId, liftId, serviceDrafts],
  );
  const serviceIds = useMemo(
    () => workOrder.services.map((svc) => svc.id),
    [workOrder.services],
  );
  const mechanicIds = useMemo(() => (mechanicId ? [mechanicId] : []), [mechanicId]);

  const hasInvalidServiceDuration = serviceDrafts.some(hasInvalidManualDuration);
  const quoteChanged = reviewedUpdatedAt !== null &&
    (reviewedUpdatedAt !== workOrder.updatedAt || workOrder.status !== 'quote');
  const canConfirm = scheduledAtStr !== '' && !loading && !settingsPending && !hasInvalidServiceDuration &&
    !!reviewedUpdatedAt && !quoteChanged;
  const outsideShift = useExactTimeOutsideShiftWarning({
    schedulingType,
    scheduledAtStr,
    durationHours: livePlannedTotal,
    mechanicIds,
  });

  const updateServiceHours = (bookingServiceId: string, raw: string) => {
    setServiceDrafts((prev) =>
      prev.map((draft) => {
        if (draft.bookingServiceId !== bookingServiceId) return draft;
        const parsed = parseDurationHours(raw);
        return {
          ...draft,
          input: raw,
          hours: parsed ?? '',
          manual: true,
        };
      }),
    );
  };

  const buildAcceptPayload = (): AcceptQuoteConfirmData | null => {
    if (!canConfirm || !reviewedUpdatedAt) return null;

    const preapprovedLimitCents = preapprovedLimitInput
      ? eurosToCents(preapprovedLimitInput)
      : undefined;

    // Assignments go on the wire only when the user changed them: the route
    // gates them behind `bookings.assign`, and an untouched prefill must not
    // turn into a 403 (#2647).
    const mechanicChanged = canAssign && mechanicId !== seededMechanicId;
    const liftChanged = canAssign && liftId !== seededLiftId;
    const perService = (value: string | null) =>
      Object.fromEntries(workOrder.services.map((svc) => [svc.bookingServiceId, value]));

    const serviceSchedules = hasServices ? changedServiceSchedules(serviceDrafts) : undefined;

    return {
      // A floating booking carries only the day: UTC midnight, so the
      // backend's queue placement sees the intended calendar day whatever the
      // browser's zone is — the same convention WorkOrderCreateModal uses.
      scheduledAt:
        schedulingType === 'floating'
          ? `${scheduledAtStr.split('T')[0] ?? scheduledAtStr}T00:00:00.000Z`
          : parseISO(scheduledAtStr).toISOString(),
      schedulingType,
      vehicleDropoffAt: vehicleDropoffAtStr ? parseISO(vehicleDropoffAtStr).toISOString() : null,
      // The pickup default mirrors scheduledAt, which for a floating booking
      // is a meaningless midnight — send only an explicitly chosen pickup.
      customerRequestedPickupAt:
        pickupAtStr && !(schedulingType === 'floating' && pickupAtStr === scheduledAtStr)
          ? parseISO(pickupAtStr).toISOString()
          : null,
      // Service-backed quotes patch planned hours per row; a no-service quote
      // still sends overall hours. 0 means "unknown" and is omitted (#2667).
      ...(hasServices
        ? (serviceSchedules ? { serviceSchedules } : {})
        : (estimatedDurationHours > 0 ? { estimatedDurationHours } : {})),
      ...(mechanicChanged
        ? { assignedMechanicId: mechanicId || null, serviceMechanics: perService(mechanicId || null) }
        : {}),
      ...(liftChanged ? { assignedLiftId: liftId || null, serviceLifts: perService(liftId || null) } : {}),
      customerWaits: customerWaits || undefined,
      allowMinorMaintenance: allowMinorMaintenance || undefined,
      jointInspectionRequested: jointInspectionRequested || undefined,
      preapprovedAdditionalWorkLimitCents: preapprovedLimitCents,
    };
  };

  const submitAccept = async (data: AcceptQuoteConfirmData) => {
    if (!reviewedUpdatedAt) return;
    try {
      await onConfirm(data, reviewedUpdatedAt);
    } catch (error) {
      const parsed = parseCertificateGateError(error);
      if (parsed) {
        pendingAcceptRef.current = data;
        setCertificateGate(parsed);
        return;
      }
      if (isCertificateOverrideForbiddenError(error)) {
        notify.error(tc('error'), t('certificateGate.forbidden'));
      }
    }
  };

  const handleConfirm = () => {
    const payload = buildAcceptPayload();
    if (!payload) return;
    void submitAccept(payload);
  };

  const handleCertificateOverrideClose = () => {
    if (overrideSubmitting) return;
    setCertificateGate(null);
    pendingAcceptRef.current = null;
  };

  const handleCertificateOverrideSubmit = async (reason: string) => {
    const pending = pendingAcceptRef.current;
    if (!pending || !reviewedUpdatedAt) return;
    setOverrideSubmitting(true);
    try {
      await onConfirm({ ...pending, skillGateOverride: { reason } }, reviewedUpdatedAt);
      setCertificateGate(null);
      pendingAcceptRef.current = null;
    } finally {
      setOverrideSubmitting(false);
    }
  };

  return (
    <>
    <ResponsiveDialog
      open={open}
      onClose={loading ? undefined : onCancel}
      mobileTitle={t('acceptQuote.dialogTitle')}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('acceptQuote.dialogTitle')}</DialogTitle>
      <DialogContent>
        {quoteVersionLabel && <Alert severity="info" sx={{ mb: 2 }}>{quoteVersionLabel}</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
        {settingsPending ? <CircularProgress size={24} /> : (
          <>
            {quoteChanged && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('acceptQuote.changedSinceReview')}
              </Alert>
            )}
            <SchedulingStep
              scheduledAtStr={scheduledAtStr}
              setScheduledAtStr={setScheduledAtStr}
              vehicleDropoffAtStr={vehicleDropoffAtStr}
              setVehicleDropoffAtStr={setVehicleDropoffAtStr}
              schedulingType={schedulingType}
              setSchedulingType={setSchedulingType}
              pickupAtStr={pickupAtStr}
              setPickupAtStr={(v) => {
                setPickupAtStr(v);
                setPickupAtTouched(true);
              }}
              estimatedDurationHours={livePlannedTotal}
              {...(hasServices
                ? {
                    durationOverride: (
                      <QuoteServicePlannedHours
                        drafts={serviceDrafts}
                        totalHours={livePlannedTotal}
                        onHoursChange={updateServiceHours}
                      />
                    ),
                  }
                : { setEstimatedDurationHours })}
              durationStepMinutes={workDurationStepMinutes}
              mechanicIds={mechanicIds}
              selectedServices={selectedServices}
              customerWaits={customerWaits}
              setCustomerWaits={setCustomerWaits}
              allowMinorMaintenance={allowMinorMaintenance}
              setAllowMinorMaintenance={setAllowMinorMaintenance}
              preapprovedLimitInput={preapprovedLimitInput}
              setPreapprovedLimitInput={setPreapprovedLimitInput}
              jointInspectionRequested={jointInspectionRequested}
              setJointInspectionRequested={setJointInspectionRequested}
              setIsDirty={() => { /* dialog has no unsaved-changes guard */ }}
              hideOutsideShiftWarning
            />
            {canAssign && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2">{t('acceptQuote.resourcesTitle')}</Typography>
                <ResourceAssignmentFields
                  serviceIds={serviceIds}
                  services={services}
                  mechanics={mechanics}
                  lifts={lifts}
                  mechanicId={mechanicId}
                  liftId={liftId}
                  onMechanicChange={setMechanicId}
                  onLiftChange={setLiftId}
                />
              </Box>
            )}
            {outsideShift && (
              <Alert severity="warning" data-testid="outside-shift-warning" sx={{ mt: 2 }}>
                {t('create.outsideShiftWarning')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {t('acceptQuote.acceptAndSchedule')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
    <CertificateOverrideDialog
      open={certificateGate !== null}
      submitting={overrideSubmitting}
      blocked={certificateGate?.blocked ?? []}
      onClose={handleCertificateOverrideClose}
      onSubmit={handleCertificateOverrideSubmit}
    />
    </>
  );
}
